import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, ShoppingBag, Loader2, MinusCircle, WifiOff } from "lucide-react";
import { recordOutcome } from "@/utils/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { addToQueue } from "@/utils/offlineQueue";
import { useAuth } from "@/context/AuthContext";

const OUTCOMES = [
  {
    key: "visited",
    label: "Fatto Visita",
    icon: CheckCircle2,
    color: "bg-emerald-600 hover:bg-emerald-700 text-white",
    border: "border-emerald-600",
    desc: "Il cliente è stato visitato",
  },
  {
    key: "not_found",
    label: "Non Trovato",
    icon: XCircle,
    color: "bg-amber-500 hover:bg-amber-600 text-white",
    border: "border-amber-500",
    desc: "Riprogrammato alla settimana successiva",
  },
  {
    key: "not_passed",
    label: "Non Passato",
    icon: MinusCircle,
    color: "bg-slate-500 hover:bg-slate-600 text-white",
    border: "border-slate-500",
    desc: "Riprogrammato alla settimana successiva",
  },
  {
    key: "order",
    label: "Fatto Ordine",
    icon: ShoppingBag,
    color: "bg-violet-600 hover:bg-violet-700 text-white",
    border: "border-violet-600",
    desc: "Ordine ricevuto dal cliente",
  },
];

export default function OutcomeModal({ appointment, open, onClose, onSuccess }) {
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [orderAmount, setOrderAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  const handleClose = () => {
    setSelected(null);
    setNotes("");
    setOrderAmount("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!selected) return;
    setLoading(true);

    const payload = {
      outcome: selected,
      outcome_notes: notes,
      order_amount: orderAmount ? parseFloat(orderAmount) : null,
      outcome_date: new Date().toISOString().split("T")[0],
    };
    const outcomeLabel = OUTCOMES.find((o) => o.key === selected)?.label;

    // Se offline → aggiungi alla coda e mostra feedback immediato
    if (!navigator.onLine) {
      addToQueue({
        method: "POST",
        path: `/api/appointments/${appointment.id}/outcome`,
        body: payload,
        token,
        description: `${outcomeLabel} — ${appointment.client?.name || "Cliente"}`,
      });
      toast.success(`Esito salvato offline: ${outcomeLabel}`, {
        description: "Sarà sincronizzato automaticamente quando torni online",
        icon: <WifiOff className="w-4 h-4" />,
        duration: 5000,
      });
      handleClose();
      onSuccess?.();
      setLoading(false);
      return;
    }

    try {
      const res = await recordOutcome(appointment.id, payload);
      const nextDate = res.data.next_date;
      const nextFormatted = nextDate
        ? format(parseISO(nextDate), "dd/MM/yyyy")
        : null;

      toast.success(`Esito registrato: ${outcomeLabel}`, {
        description: nextFormatted ? `Prossima visita: ${nextFormatted}` : undefined,
      });
      handleClose();
      onSuccess?.();
    } catch (err) {
      toast.error("Errore nella registrazione dell'esito");
    } finally {
      setLoading(false);
    }
  };

  const dateFormatted = appointment?.scheduled_date
    ? format(parseISO(appointment.scheduled_date), "EEEE d MMMM yyyy", { locale: it })
    : "";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="outcome-modal"
        className="w-full max-w-md mx-auto sm:rounded-2xl flex flex-col max-h-[90dvh] p-0 gap-0"
        style={{ zoom: "0.88" }}
      >
        {/* Header fisso */}
        <div className="px-6 pt-6 pb-3 flex-shrink-0">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
              Registra Esito Visita
            </DialogTitle>
            {appointment && (
              <div className="text-sm text-slate-500 mt-1">
                <span className="font-semibold text-slate-800">
                  {appointment.client?.name}
                </span>
                {appointment.client?.company && (
                  <span className="text-slate-500"> · {appointment.client.company}</span>
                )}
                <br />
                <span className="capitalize">{dateFormatted}</span>
              </div>
            )}
          </DialogHeader>
        </div>

        {/* Area scrollabile */}
        <div className="flex-1 overflow-y-auto px-6 pb-2 space-y-3">
          {OUTCOMES.map(({ key, label, icon: Icon, color, border, desc }) => (
            <button
              key={key}
              data-testid={`outcome-btn-${key}`}
              onClick={() => setSelected(key)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all font-semibold text-left ${
                selected === key
                  ? `${color} ${border} scale-[1.01] shadow-md`
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
              }`}
            >
              <Icon className={`w-5 h-5 flex-shrink-0 ${selected === key ? "text-white" : "text-slate-400"}`} />
              <div>
                <div className="text-sm font-semibold">{label}</div>
                <div className={`text-xs ${selected === key ? "text-white/80" : "text-slate-400"}`}>
                  {desc}
                </div>
              </div>
            </button>
          ))}

          {selected === "order" && (
            <div className="slide-up">
              <Label htmlFor="order-amount" className="text-sm font-medium text-slate-700">
                Importo ordine (€)
              </Label>
              <Input
                id="order-amount"
                data-testid="order-amount-input"
                type="number"
                placeholder="0.00"
                value={orderAmount}
                onChange={(e) => setOrderAmount(e.target.value)}
                className="mt-1"
              />
            </div>
          )}

          <div>
            <Label htmlFor="outcome-notes" className="text-sm font-medium text-slate-700">
              Nota per la prossima visita
            </Label>
            <Textarea
              id="outcome-notes"
              data-testid="outcome-notes-input"
              placeholder="Es: chiedere conferma ordine, portare campioni nuovi..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 resize-none"
              rows={2}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Questa nota verrà salvata sul cliente come promemoria
            </p>
          </div>
        </div>

        {/* Bottoni fissi in fondo */}
        <div className="flex gap-2 px-6 py-4 border-t border-slate-100 flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex-1"
            data-testid="outcome-cancel-btn"
          >
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selected || loading}
            data-testid="outcome-submit-btn"
            className="flex-1 bg-[#0F172A] hover:bg-[#1E293B] text-white"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
